import axios from "axios";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { Panel } from "primereact/panel";
import { Toolbar } from "primereact/toolbar";
import React, { useState, useContext, useRef } from "react";
import Endpoint from "../../../endpoints/Endpoint";
import EntityOperation from "../../../infrastructure/EnumEntityOperation";
import Labels from "../../../infrastructure/Labels_sr_Latn_RS";
import { axiosConfig, useEffectOnce } from "../../../utils/Utils";
import CrudUloga from "./CrudUloga";
import { AppContext } from "../../Store";

export default function CrudUlogaList() {
  const [selectedRow, setSelectedRow] = useState([]);
  const [selectedUloga, setSelectedUloga] = useState();
  const [ulogeList, setUlogeList] = useState();
  const [entityOperation, setEntityOperation] = useState<string>("");
  const [displayDialog, setDisplayDialog] = useState(false);
  const [ulogaToChange, setUlogaToChange] = useState<any>();
  const { authData } = useContext(AppContext);

  const [messages, setMessages] = useState<Messages>();
  const dialogRef = useRef<any>();
  const [index, setIndex] = useState<number>(0);
  const ulogaIndex: number = 0;

  useEffectOnce(() => {
    fetchData();
  });

  const fetchData = () => {
    axios
      .get(
        Endpoint.ULOGA + "/search/findByAktivan",
        axiosConfig(authData.token, {
          page: 0,
          size: 50000000,
          aktivan: true,
        })
      )
      .then((ulogaCollection) => {
        setUlogeList(ulogaCollection.data._embedded.ulogas);
      });
  };

  const showSuccess = (summary: string, detail: string) => {
    if (messages) {
      messages.replace({
        severity: "success",
        summary: summary,
        detail: detail,
        closable: true,
        sticky: true,
      });
    }
  };

  const createUloga = (uloga: any) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .post(Endpoint.ULOGA, uloga, axiosConfig(authData.token))
        .then(() => {
          closeDialog();
          showSuccess(Labels.TITLE_MESSAGES_USPESNO_KREIRANO, uloga.ime);
          fetchData();
          resolve();
        })
        .catch((error: any) => {
          reject(error.response.data.message);
        });
    });
  };

  const updateUloga = (uloga: any) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .patch(Endpoint.ULOGA + `/${uloga.id}`, uloga, axiosConfig(authData.token))
        .then(() => {
          closeDialog();
          showSuccess(Labels.TITLE_MESSAGES_USPESNO_IZMENJENO, uloga.ime);
          fetchData();
          resolve();
        })
        .catch((error: any) => {
          reject(error.response.data.message);
        });
    });
  };

  const deleteUloga = (uloga: any) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .patch(
          Endpoint.ULOGA + `/${uloga.id}`,
          {
            aktivan: false,
          },
          axiosConfig(authData.token)
        )
        .then(() => {
          closeDialog();
          showSuccess(Labels.TITLE_MESSAGES_USPESNO_OBRISANO, uloga.ime);
          fetchData();
          resolve();
        })
        .catch((error: any) => {
          reject(error.response.data.message);
        });
    });
  };

  const dialogHeader = () => {
    const display = ulogaToChange ? ulogaToChange.ime : "";

    switch (entityOperation) {
      case EntityOperation.CREATE:
        return Labels.TITLE_DIALOG_KREIRANJE_ULOGE;
      case EntityOperation.DELETE:
        return Labels.TITLE_DIALOG_BRISANJE_ULOGE + display;
      case EntityOperation.UPDATE:
        return Labels.TITLE_DIALOG_IZMENA_ULOGE + display;
      case EntityOperation.READ:
        return Labels.TITLE_DIALOG_DETALJI_ULOGE + display;
      default:
        return "";
    }
  };

  const openDialog = (entityOperation: string) => {
    let r;
    switch (entityOperation) {
      case EntityOperation.UPDATE:
      case EntityOperation.READ:
      case EntityOperation.DELETE:
        r = selectedUloga;
        break;
    }
    setEntityOperation(entityOperation);
    setUlogaToChange(r);
    setDisplayDialog(true);
  };

  const closeDialog = () => {
    setIndex(0);
    setDisplayDialog(false);
    setSelectedUloga(undefined);
    setSelectedRow([]);
  };

  const onCancel = () => {
    closeDialog();
  };

  const leftContentsDialog = () => (
    <React.Fragment>
      {entityOperation === EntityOperation.CREATE && (
        <Button
          label={Labels.BUTTON_KREIRAJ}
          icon="pi pi-save"
          className="p-button-success"
          onClick={() => {
            dialogRef.current.onCreate();
          }}
        />
      )}
      {entityOperation === EntityOperation.UPDATE && index === ulogaIndex && (
        <Button
          label={Labels.BUTTON_IZMENI}
          icon="pi pi-inbox"
          className="p-button-warning"
          onClick={() => {
            dialogRef.current.onUpdate();
          }}
        />
      )}
      {entityOperation === EntityOperation.DELETE && (
        <Button
          label={Labels.BUTTON_OBRISI}
          icon="pi pi-inbox"
          className="p-button-danger"
          onClick={() => {
            dialogRef.current.onDelete();
          }}
        />
      )}
    </React.Fragment>
  );

  const rightContentsDialog = () => <Button label={Labels.BUTTON_ODUSTANI} icon="pi pi-times" className="p-button-danger" onClick={onCancel} />;

  const leftContents = () => (
    <React.Fragment>
      <Button label={Labels.BUTTON_KREIRAJ} icon="pi pi-plus" className="p-button-success" onClick={() => openDialog(EntityOperation.CREATE)} />
      <Button disabled={!selectedUloga} label={Labels.BUTTON_IZMENI} icon="pi pi-upload" className="p-button-warning" onClick={() => openDialog(EntityOperation.UPDATE)} />
      <Button disabled={!selectedUloga} label={Labels.BUTTON_DETALJI} icon="pi pi-bars" className="p-button-info" onClick={() => openDialog(EntityOperation.READ)} />
    </React.Fragment>
  );

  const rightContents = () => <Button disabled={!selectedUloga} label={Labels.BUTTON_OBRISI} icon="pi pi-inbox" className="p-button-danger" onClick={() => openDialog(EntityOperation.DELETE)} />;

  const dialogFooter = () => <Toolbar left={leftContentsDialog} right={rightContentsDialog} />;

  return (
    <div className="layout-role">
      <div className="col-12">
        <Messages
          ref={(el: any) => {
            setMessages(el);
          }}
        />
      </div>
      <Panel header={Labels.TITLE_PANEL_CRUD_ULOGA} className="panel-roles-list">
        <Toolbar left={leftContents} right={rightContents} />
        <div>
          <DataTable
            filterDisplay="row"
            value={ulogeList}
            paginator={true}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate={Labels.PAGINATION_SHOWING + " {first} " + Labels.DASH + " {last} " + Labels.PAGINATION_OF + " {totalRecords} "}
            rows={20}
            rowsPerPageOptions={[5, 10, 20]}
            selectionMode="single"
            selection={selectedRow}
            onSelectionChange={(e) => setSelectedRow(e.value)}
            onRowSelect={(e) => setSelectedUloga(e.data)}
            alwaysShowPaginator={false}
            emptyMessage={Labels.LABEL_NEMA_PODATAKA}
          >
            <Column field={"ime"} header={Labels.COLUMN_HEADER_IME} filterMatchMode="contains" showFilterMenu={false} filter sortable />
            <Column className="p-sortable-column" field={"opis"} header={Labels.COLUMN_HEADER_OPIS} filterMatchMode="contains" showFilterMenu={false} filter />
            <Column className="column-width-200px" field={"numerickaVrednost"} header={Labels.COLUMN_HEADER_NUMERICKA_VREDNOST} filterMatchMode="contains" showFilterMenu={false} filter sortable />
          </DataTable>
          <Dialog header={dialogHeader()} visible={displayDialog} onHide={closeDialog} style={{ width: "90%" }} footer={dialogFooter()}>
            <CrudUloga
              ulogaOperation={entityOperation}
              uloga={ulogaToChange}
              onCreateUloga={createUloga}
              onUpdateUloga={updateUloga}
              onDeleteUloga={deleteUloga}
              onCancel={onCancel}
              dialogRef={dialogRef}
              setIndex={setIndex}
              index={index}
            />
          </Dialog>
        </div>
      </Panel>
    </div>
  );
}
